/**
 * Fixed pages for the packet (Abner, Oct 16: "Fixed pages"). Every section
 * starts a page; its units are measured off-screen and packed onto pages
 * whose body is the measured page box less the band, the title and the
 * footer. Matt's rules (comment b9b2a8d6):
 *
 * - "a page never clips": a unit that does not fit moves to the next page;
 * - "Breaks fall between sections (a whole group with its grey header moves
 *   together), never mid-group": a group that fits on a fresh page moves
 *   whole; only a group taller than a page splits, and its band repeats on
 *   the next page, "(continued)";
 * - "Stop sheets and route blocks are the exceptions: they always stay whole
 *   on one page, so we tighten row spacing first": those sections switch to
 *   the compact spacing when the normal one runs past a page.
 *
 * A band never ends a page, a total never starts one, and a continuation page
 * opens with the section's repeat units (column labels, a heading), each once
 * the unit it copies has printed: the pack list repeats the production
 * table's head only on the pages that table runs onto, never above a milk
 * table that spilled. Pages pack to `PACKET_FILL` of the measured body, the
 * slack for a PDF renderer whose fonts run a little taller than the browser
 * that measured them; a page that still runs over prints an extra sheet,
 * never a cut-off row.
 *
 * Pure; unit-tested.
 */

import { isSubBand, type PacketModel, type PacketSection, type PacketUnit } from './units'

/**
 * Pack to this share of the measured page body. Measured Oct 24 against printing-press (the PDFs product): its text
 * runs 1.1–1.6% taller than the browser's over a full stop sheet or recipe page, so 4% of slack is 2.5× the drift.
 */
export const PACKET_FILL = 0.96

/** A page body when a frame was not measured: the 1004px page box less band, title and footer. */
export const DEFAULT_BODY_HEIGHT = 880

export interface PacketHeights {
  /** Each section's page body height, from a frame probe with no content. */
  body: ReadonlyMap<string, number>
  /** Each unit's height at normal spacing, repeat units included. */
  unit: ReadonlyMap<string, number>
  /** Each unit's height at compact spacing (measured for `preferOnePage` sections only). */
  compact: ReadonlyMap<string, number>
}

export interface LaidOutItem {
  unitId: string
  /** A group band or a repeat unit printed again atop a continuation page: a band says "(continued)". */
  continued: boolean
}

export interface LaidOutPage {
  sectionId: string
  /** 0-based page within its section, and the section's page count ("2 OF 3"). */
  index: number
  count: number
  /** 1-based page number in the whole packet. */
  number: number
  items: LaidOutItem[]
  /** The section prints at compact spacing. */
  compact: boolean
  /**
   * "Kitchen 4 of 17" (`index`/`count` null for an uncounted group: "Stop 1 · School copy"); a section that says
   * its length carries it in the label: "Driver route · 1 page".
   */
  footer: { label: string; index: number | null; count: number | null }
}

export interface PacketLayout {
  pages: LaidOutPage[]
  totalPages: number
  /** First and last page number of every section. */
  ranges: ReadonlyMap<string, { first: number; last: number }>
}

interface UnitGroup {
  /** Stand-alone bands that keep with this group (a column header, an intro line). Printed once. */
  prefix: PacketUnit[]
  /**
   * The group's own leading bands (its grey band, its column labels): printed
   * again atop a continuation page. A sub-band ("K-8", "SIDES · 8") is not
   * head: it opens a run of rows inside the group, and a continuation page
   * repeats the sub-band of the rows it carries on.
   */
  head: PacketUnit[]
  rest: PacketUnit[]
}

/** Consecutive units of one group, with any stand-alone bands before them attached. */
export function groupUnits(units: readonly PacketUnit[]): UnitGroup[] {
  const groups: UnitGroup[] = []
  let prefix: PacketUnit[] = []
  let i = 0
  while (i < units.length) {
    const unit = units[i]!
    if (unit.group === null) {
      if (unit.role === 'band') {
        prefix.push(unit)
      } else {
        groups.push({ prefix, head: [], rest: [unit] })
        prefix = []
      }
      i += 1
      continue
    }
    const run: PacketUnit[] = []
    while (i < units.length && units[i]!.group === unit.group) {
      run.push(units[i]!)
      i += 1
    }
    let headEnd = 0
    while (headEnd < run.length && run[headEnd]!.role === 'band' && !isSubBand(run[headEnd]!)) {
      headEnd += 1
    }
    groups.push({ prefix, head: run.slice(0, headEnd), rest: run.slice(headEnd) })
    prefix = []
  }
  if (prefix.length > 0) {
    groups.push({ prefix: [], head: [], rest: prefix })
  }
  return groups
}

/** One section's pages: the unit ids on each, in print order. */
export function paginateSection(
  section: Pick<PacketSection, 'units' | 'repeat'>,
  height: (unitId: string) => number,
  cap: number,
): LaidOutItem[][] {
  const pages: LaidOutItem[][] = []
  const sum = (units: readonly PacketUnit[]) => units.reduce((total, unit) => total + height(unit.id), 0)

  let page: LaidOutItem[] = []
  let used = 0
  let content = 0
  // True while the page holds only heading blocks (`keepWithNext`): nothing may leave them alone on it.
  let headingsOnly = true
  // Every stream unit printed so far: a repeat copy heads a page only once its original has.
  const placed = new Set<string>()
  const repeats = () => section.repeat.filter((unit) => !unit.twinOf || placed.has(unit.twinOf))
  const open = () => {
    pages.push(page)
    const heads = repeats()
    page = heads.map((unit) => ({ unitId: unit.id, continued: true }))
    used = sum(heads)
    content = 0
    headingsOnly = true
  }
  const place = (unit: PacketUnit, continued = false) => {
    page.push({ unitId: unit.id, continued })
    placed.add(unit.id)
    used += height(unit.id)
    content += 1
    headingsOnly = headingsOnly && unit.keepWithNext === true
  }
  const fits = (h: number) => used + h <= cap
  // A page holding only a header keeps what follows: the group starts under it and continues.
  const mayLeave = () => content > 0 && !headingsOnly

  for (const group of groupUnits(section.units)) {
    const all = [...group.prefix, ...group.head, ...group.rest]
    const total = sum(all)
    if (fits(total)) {
      all.forEach((unit) => place(unit))
      continue
    }
    // A group that does not fit splits where it is, its head said again on the next page, rather than leave this
    // page mostly blank (Sheila, Oct 23: a 14-stop cover printed one near-empty page and two half pages). It moves
    // whole only when it is short (under 4 rows) or this page cannot take its head and 2 rows, and a fresh page
    // holds it.
    const splitsHere = group.rest.length >= 4 && fits(sum([...group.prefix, ...group.head, ...group.rest.slice(0, 2)]))
    if (mayLeave() && !splitsHere && total <= cap - sum(repeats())) {
      open()
      all.forEach((unit) => place(unit))
      continue
    }
    // Taller than a page: split it, the band and its first row together.
    const lead = [...group.prefix, ...group.head]
    const firstRow = group.rest[0]
    if (mayLeave() && !fits(sum(lead) + (firstRow ? height(firstRow.id) : 0))) {
      open()
    }
    lead.forEach((unit) => place(unit))
    let rowsHere = 0
    // The sub-band ("K-8", "SIDES · 8") the rows being placed belong to.
    let subBand: PacketUnit | null = null
    group.rest.forEach((unit, j) => {
      const next = group.rest[j + 1]
      let need = height(unit.id)
      if (unit.role === 'band' && next) {
        need += height(next.id)
      }
      if (next && next.role === 'tail' && j + 2 === group.rest.length) {
        need += height(next.id)
      }
      if (!fits(need) && rowsHere > 0) {
        open()
        group.head.forEach((band, k) => place(band, k === 0))
        // The rows carry on under their own sub-band, said again.
        if (subBand && !isSubBand(unit)) {
          place(subBand, true)
        }
        rowsHere = 0
      }
      place(unit)
      if (isSubBand(unit)) {
        subBand = unit
      }
      rowsHere += 1
    })
  }
  if (content > 0 || pages.length === 0) {
    pages.push(page)
  }
  return pages
}

/** An uncounted footer's words: its name, and its section's length when it says it ("Driver route · 1 page"). */
function footerLabel(section: PacketSection, pages: number): string {
  return section.footer.pages
    ? `${section.footer.label} · ${pages} ${pages === 1 ? 'page' : 'pages'}`
    : section.footer.label
}

function unitsHeight(units: readonly PacketUnit[], height: (unitId: string) => number): number {
  return units.reduce((sum, unit) => sum + height(unit.id), 0)
}

/** The packet on fixed pages: every section paginated, numbered and footed. */
export function layoutPacket(model: PacketModel, heights: PacketHeights, fill: number = PACKET_FILL): PacketLayout {
  const laid: Array<Omit<LaidOutPage, 'number' | 'footer'> & { section: PacketSection }> = []
  for (const section of model.sections) {
    const cap = (heights.body.get(section.id) ?? DEFAULT_BODY_HEIGHT) * fill
    const normal = (id: string) => heights.unit.get(id) ?? 0
    let height = normal
    let compact = false
    if (section.preferOnePage && unitsHeight(section.units, normal) > cap) {
      compact = true
      height = (id: string) => heights.compact.get(id) ?? normal(id)
    }
    const pages = paginateSection(section, height, cap)
    pages.forEach((items, index) =>
      laid.push({ section, sectionId: section.id, index, count: pages.length, items, compact }),
    )
  }

  const groupTotals = new Map<string, number>()
  for (const page of laid) {
    groupTotals.set(page.section.footer.key, (groupTotals.get(page.section.footer.key) ?? 0) + 1)
  }
  const groupSeen = new Map<string, number>()
  const ranges = new Map<string, { first: number; last: number }>()
  const pages: LaidOutPage[] = laid.map(({ section, ...page }, i) => {
    const number = i + 1
    const seen = (groupSeen.get(section.footer.key) ?? 0) + 1
    groupSeen.set(section.footer.key, seen)
    const range = ranges.get(section.id)
    ranges.set(section.id, { first: range?.first ?? number, last: number })
    return {
      ...page,
      number,
      footer: section.footer.counted
        ? { label: section.footer.label, index: seen, count: groupTotals.get(section.footer.key)! }
        : { label: footerLabel(section, page.count), index: null, count: null },
    }
  })
  return { pages, totalPages: pages.length, ranges }
}

/** "4–6" for a TOC entry spanning sections; "17" for one page; null when none printed. */
export function pageRangeLabel(ranges: PacketLayout['ranges'], sectionIds: readonly string[]): string | null {
  let first: number | null = null
  let last: number | null = null
  for (const id of sectionIds) {
    const range = ranges.get(id)
    if (!range) {
      continue
    }
    first = first === null ? range.first : Math.min(first, range.first)
    last = last === null ? range.last : Math.max(last, range.last)
  }
  if (first === null || last === null) {
    return null
  }
  return first === last ? String(first) : `${first}–${last}`
}
